import { z } from "zod";
import { isoDateTimeSchema } from "./dates";

// Enums
export const SurveyAnswerTypeEnum = z.enum(["YES_NO", "MULTIPLE_CHOICE", "TEXT", "STAR_RATING"]);

// Base schemas
export const surveyChoiceSchema = z.object({
  id: z.number(),
  choiceText: z.string(),
  questionId: z.number(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});

export const surveyQuestionSchema = z.object({
  id: z.number(),
  question: z.string(),
  answerType: SurveyAnswerTypeEnum,
  conditionalOnId: z.number().nullable(),
  condition: z.string().nullable(),
  get conditionals() {
    return z.array(surveyQuestionSchema).optional();
  },
  choices: z.array(surveyChoiceSchema).optional(),
  isActive: z.boolean(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});

// Input schemas
export const createSurveyChoiceInputSchema = z.object({
  choiceText: z.string(),
});

export const createSurveyQuestionInputSchema = z.object({
  question: z.string(),
  answerType: SurveyAnswerTypeEnum,
  conditionalOnId: z.number().optional(),
  choices: z.array(createSurveyChoiceInputSchema).optional(),
});

export const surveyQuestionResponseInputSchema = z.object({
  questionId: z.number(),
  choiceId: z.number().optional(),
  responseText: z.string().optional(),
});

export const submitSurveyResponseInputSchema = z.object({
  deliveryId: z.number(),
  responses: z.array(surveyQuestionResponseInputSchema),
});

// Response/Analytics schemas
export const surveyResponseSchema = z.object({
  id: z.number(),
  deliveryId: z.number(),
  questionId: z.number(),
  choiceId: z.number().nullable(),
  responseText: z.string().nullable(),
  createdAt: isoDateTimeSchema,
});

export const responseAnalyticsSchema = z.object({
  choiceId: z.number().optional(),
  choiceText: z.string(),
  count: z.number(),
  percentage: z.number(),
});

export const questionAnalyticsSchema = z.object({
  questionId: z.number(),
  question: z.string(),
  responses: z.array(responseAnalyticsSchema),
});

export const surveyAnalyticsSchema = z.object({
  totalResponses: z.number(),
  responseRate: z.number(),
  questionAnalytics: z.array(questionAnalyticsSchema),
});

// Types
export type SurveyAnswerType = z.infer<typeof SurveyAnswerTypeEnum>;
export type SurveyChoice = z.infer<typeof surveyChoiceSchema>;
export type SurveyQuestion = z.infer<typeof surveyQuestionSchema>;
export type CreateSurveyChoiceInput = z.infer<typeof createSurveyChoiceInputSchema>;
export type CreateSurveyQuestionInput = z.infer<typeof createSurveyQuestionInputSchema>;
export type SurveyQuestionResponseInput = z.infer<typeof surveyQuestionResponseInputSchema>;
export type SubmitSurveyResponseInput = z.infer<typeof submitSurveyResponseInputSchema>;
export type SurveyResponse = z.infer<typeof surveyResponseSchema>;
export type ResponseAnalytics = z.infer<typeof responseAnalyticsSchema>;
export type QuestionAnalytics = z.infer<typeof questionAnalyticsSchema>;
export type SurveyAnalytics = z.infer<typeof surveyAnalyticsSchema>;
